import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import axios from 'axios';
import { 
  Download, Share2, X, Mail, Phone, MapPin, Calendar, 
  User, GraduationCap, Briefcase, Globe, Heart, FileText,
  MessageSquare, Video, Clock, Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const FieldGroup = ({ title, icon: Icon, children }) => (
  <div className="mb-6">
    <h3 className="font-semibold text-lg flex items-center gap-2 mb-3 text-gray-800 border-b pb-2">
      {Icon && <Icon className="w-5 h-5 text-cyan-600" />}
      {title}
    </h3>
    <div className="grid md:grid-cols-2 gap-4">
      {children}
    </div>
  </div>
);

const Field = ({ label, value, fullWidth = false }) => (
  <div className={fullWidth ? 'md:col-span-2' : ''}>
    <p className="text-sm text-gray-500">{label}</p>
    <p className="font-medium text-gray-900">{value || 'Not provided'}</p>
  </div>
);

const LongTextField = ({ label, value }) => (
  <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
    <p className="text-sm font-medium text-gray-600 mb-2">{label}</p>
    <p className="text-gray-900 whitespace-pre-wrap">{value || 'Not provided'}</p>
  </div>
);

const ApplicationViewer = ({ application, type, onClose, onStatusChange }) => {
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareMessage, setShareMessage] = useState('');
  const [sharing, setSharing] = useState(false);
  const contentRef = useRef(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Generate PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;
    const lineHeight = 7;
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    // Helper function to add text with word wrap
    const addWrappedText = (text, x, y, maxWidth, lineHeight) => {
      const lines = doc.splitTextToSize(text || 'Not provided', maxWidth);
      doc.text(lines, x, y);
      return y + (lines.length * lineHeight);
    };

    // Header
    doc.setFillColor(8, 145, 178); // cyan-600
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text(`${type === 'cub' ? 'Cub' : 'Lion (Mentor)'} Application`, margin, 25);
    doc.setFontSize(10);
    doc.text(`Submitted: ${formatDate(application.submittedAt)}`, margin, 35);
    
    // Status badge
    doc.setFillColor(application.status === 'accepted' ? 34 : application.status === 'pending' ? 234 : 59, 
                     application.status === 'accepted' ? 197 : application.status === 'pending' ? 179 : 130, 
                     application.status === 'accepted' ? 94 : application.status === 'pending' ? 8 : 246);
    doc.roundedRect(pageWidth - 50, 15, 35, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.text(application.status.toUpperCase(), pageWidth - 48, 22);

    y = 55;
    doc.setTextColor(0, 0, 0);

    // Personal Information
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Personal Information', margin, y);
    y += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const personalFields = [
      ['Full Name', `${application.firstName} ${application.lastName}`],
      ['Email', application.email],
      ['Phone', application.phone],
      ['Address', application.address],
      ['Date of Birth', application.dateOfBirth],
      ['Gender', application.gender]
    ];

    personalFields.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(`${label}:`, margin, y);
      doc.setFont(undefined, 'normal');
      doc.text(value || 'Not provided', margin + 40, y);
      y += lineHeight;
    });

    y += 5;

    // Background
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Background', margin, y);
    y += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const backgroundFields = [
      ['Country of Birth', application.countryOfBirth],
      ['Country of Residence', application.countryOfResidence],
      ['Minority Status', application.isMinority],
      ['Minority Country', application.minorityCountry]
    ];

    backgroundFields.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(`${label}:`, margin, y);
      doc.setFont(undefined, 'normal');
      doc.text(value || 'Not provided', margin + 50, y);
      y += lineHeight;
    });

    y += 5;

    if (type === 'cub') {
      // Education & Career
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Education & Career', margin, y);
      y += 10;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');

      const eduFields = [
        ['Education Level', application.ownEducation],
        ['GPA', application.gpa],
        ['Currently Student', application.isStudent],
        ['Graduation Info', application.graduationInfo],
        ['Occupation', application.ownOccupation]
      ];

      eduFields.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(`${label}:`, margin, y);
        doc.setFont(undefined, 'normal');
        doc.text(value || 'Not provided', margin + 45, y);
        y += lineHeight;
      });

      y += 5;

      // Family Background
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Family Background', margin, y);
      y += 10;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');

      const familyFields = [
        ['Parents Country of Birth', application.parentsCountryOfBirth],
        ['Parents Country of Residence', application.parentsCountryOfResidence],
        ["Mother's Education", application.motherEducation],
        ["Father's Education", application.fatherEducation],
        ["Mother's Occupation", application.motherOccupation],
        ["Father's Occupation", application.fatherOccupation]
      ];

      familyFields.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(`${label}:`, margin, y);
        doc.setFont(undefined, 'normal');
        doc.text(value || 'Not provided', margin + 60, y);
        y += lineHeight;
      });

      // Add new page for responses
      doc.addPage();
      y = 20;

      // Responses
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Application Responses', margin, y);
      y += 10;
      doc.setFontSize(10);

      const responses = [
        ['How did you hear about us?', application.howHeard],
        ['Why do you want to join?', application.whyJoin],
        ['Struggles you have overcome', application.struggles],
        ['One person you would have dinner with', application.dinnerPerson],
        ['How would you like to be remembered?', application.eulogy]
      ];

      responses.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(label, margin, y);
        y += lineHeight;
        doc.setFont(undefined, 'normal');
        y = addWrappedText(value, margin, y, contentWidth, lineHeight);
        y += 8;

        if (y > 270) {
          doc.addPage();
          y = 20;
        }
      });

      if (application.videoUrl) {
        doc.setFont(undefined, 'bold');
        doc.text('Video Submission:', margin, y);
        y += lineHeight;
        doc.setFont(undefined, 'normal');
        doc.setTextColor(8, 145, 178);
        doc.text(application.videoUrl, margin, y);
        doc.setTextColor(0, 0, 0);
      }

    } else {
      // Lion-specific fields
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Mentorship Information', margin, y);
      y += 10;
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');

      const mentorFields = [
        ['Occupation', application.occupation],
        ['Time Commitment', application.timeCommitment],
        ['Number of Cubs', application.numberOfCubs],
        ['How did you hear about us?', application.howHeard]
      ];

      mentorFields.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(`${label}:`, margin, y);
        doc.setFont(undefined, 'normal');
        doc.text(value || 'Not provided', margin + 55, y);
        y += lineHeight;
      });

      y += 10;

      // Responses
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Application Responses', margin, y);
      y += 10;
      doc.setFontSize(10);

      const responses = [
        ['Why do you want to be a mentor?', application.whyMentor],
        ['Experience & Hardships', application.experience],
        ['Passions', application.passions]
      ];

      responses.forEach(([label, value]) => {
        doc.setFont(undefined, 'bold');
        doc.text(label, margin, y);
        y += lineHeight;
        doc.setFont(undefined, 'normal');
        y = addWrappedText(value, margin, y, contentWidth, lineHeight);
        y += 8;

        if (y > 270) {
          doc.addPage();
          y = 20;
        }
      });
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated by Lions Unleashed Admin - ${new Date().toLocaleString()}`, margin, doc.internal.pageSize.getHeight() - 10);

    // Save PDF
    const fileName = `${type}-application-${application.firstName}-${application.lastName}-${application.id.slice(0, 8)}.pdf`;
    doc.save(fileName);
    toast.success('PDF exported successfully!');
  };

  // Share application via email
  const shareApplication = async () => {
    if (!shareEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSharing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/applications/${type}/${application.id}/share`, {
        email: shareEmail,
        message: shareMessage
      });
      toast.success(`Application shared with ${shareEmail}`);
      setShowShareDialog(false);
      setShareEmail('');
      setShareMessage('');
    } catch (error) {
      toast.error('Failed to share application');
    }
    setSharing(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4" ref={contentRef}>
        {/* Header */}
        <div className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">{application.firstName} {application.lastName}</h2>
              <p className="text-cyan-100">{type === 'cub' ? 'Cub Application' : 'Lion (Mentor) Application'}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(application.status)}`}>
                {application.status}
              </span>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <p className="text-cyan-100 text-sm mt-2">
            Submitted: {formatDate(application.submittedAt)}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-2 p-4 border-b bg-gray-50">
          <Button onClick={exportToPDF} variant="outline" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export PDF
          </Button>
          <Button onClick={() => setShowShareDialog(true)} variant="outline" className="flex items-center gap-2">
            <Share2 className="w-4 h-4" />
            Share Application
          </Button>
          <div className="flex-1" />
          <Button 
            onClick={() => onStatusChange('pending')} 
            size="sm"
            className={application.status === 'pending' ? 'bg-yellow-500' : 'bg-yellow-100 text-yellow-800'}
          >
            Pending
          </Button>
          <Button 
            onClick={() => onStatusChange('reviewed')} 
            size="sm"
            className={application.status === 'reviewed' ? 'bg-blue-500' : 'bg-blue-100 text-blue-800'}
          >
            Reviewed
          </Button>
          <Button 
            onClick={() => onStatusChange('accepted')} 
            size="sm"
            className={application.status === 'accepted' ? 'bg-green-500' : 'bg-green-100 text-green-800'}
          >
            Accept
          </Button>
          <Button 
            onClick={() => onStatusChange('rejected')} 
            size="sm"
            className={application.status === 'rejected' ? 'bg-red-500' : 'bg-red-100 text-red-800'}
          >
            Reject
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {/* Personal Information */}
          <FieldGroup title="Personal Information" icon={User}>
            <Field label="First Name" value={application.firstName} />
            <Field label="Last Name" value={application.lastName} />
            <Field label="Email" value={application.email} />
            <Field label="Phone" value={application.phone} />
            <Field label="Address" value={application.address} fullWidth />
            <Field label="Date of Birth" value={application.dateOfBirth} />
            <Field label="Gender" value={application.gender} />
          </FieldGroup>

          {/* Background */}
          <FieldGroup title="Background" icon={Globe}>
            <Field label="Country of Birth" value={application.countryOfBirth} />
            <Field label="Country of Residence" value={application.countryOfResidence} />
            <Field label="Minority Status" value={application.isMinority} />
            {application.minorityCountry && (
              <Field label="Minority Country" value={application.minorityCountry} />
            )}
          </FieldGroup>

          {type === 'cub' && (
            <>
              {/* Education & Career */}
              <FieldGroup title="Education & Career" icon={GraduationCap}>
                <Field label="Education Level" value={application.ownEducation} />
                <Field label="GPA" value={application.gpa} />
                <Field label="Currently a Student" value={application.isStudent} />
                <Field label="Graduation Info" value={application.graduationInfo} />
                <Field label="Occupation" value={application.ownOccupation} />
              </FieldGroup>

              {/* Family Background */}
              <FieldGroup title="Family Background" icon={Users}>
                <Field label="Parents' Country of Birth" value={application.parentsCountryOfBirth} />
                <Field label="Parents' Country of Residence" value={application.parentsCountryOfResidence} />
                <Field label="Mother's Education" value={application.motherEducation} />
                <Field label="Father's Education" value={application.fatherEducation} />
                <Field label="Mother's Occupation" value={application.motherOccupation} />
                <Field label="Father's Occupation" value={application.fatherOccupation} />
              </FieldGroup>

              {/* Responses */}
              <FieldGroup title="Application Responses" icon={MessageSquare}>
                <LongTextField label="How did you hear about us?" value={application.howHeard} />
                <LongTextField label="Why do you want to join Lions Unleashed?" value={application.whyJoin} />
                <LongTextField label="Struggles you have overcome" value={application.struggles} />
                <LongTextField label="If you could have dinner with one person (alive or dead), who would it be and why?" value={application.dinnerPerson} />
                <LongTextField label="How would you like to be remembered? What would your eulogy say?" value={application.eulogy} />
                {application.videoUrl && (
                  <div className="md:col-span-2">
                    <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
                      <Video className="w-4 h-4" /> Video Submission
                    </p>
                    <a href={application.videoUrl} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">
                      {application.videoUrl}
                    </a>
                  </div>
                )}
              </FieldGroup>
            </>
          )}

          {type === 'lion' && (
            <>
              {/* Mentorship Information */}
              <FieldGroup title="Mentorship Information" icon={Briefcase}>
                <Field label="Occupation" value={application.occupation} />
                <Field label="How did you hear about us?" value={application.howHeard} />
                <Field label="Time Commitment" value={application.timeCommitment} />
                <Field label="Number of Cubs to Mentor" value={application.numberOfCubs} />
              </FieldGroup>

              {/* Responses */}
              <FieldGroup title="Application Responses" icon={MessageSquare}>
                <LongTextField label="Why do you want to be a mentor?" value={application.whyMentor} />
                <LongTextField label="Experience & Hardships" value={application.experience} />
                <LongTextField label="Passions" value={application.passions} />
              </FieldGroup>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Share Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Share {application.firstName} {application.lastName}'s application via email.
            </p>
            <div>
              <Label>Recipient Email *</Label>
              <Input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder="recipient@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Message (optional)</Label>
              <Input
                value={shareMessage}
                onChange={(e) => setShareMessage(e.target.value)}
                placeholder="Add a personal message..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShareDialog(false)}>Cancel</Button>
            <Button onClick={shareApplication} disabled={sharing} className="bg-gradient-to-r from-cyan-500 to-blue-600">
              {sharing ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicationViewer;
